import { AsyncPipe, DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Store } from '@ngxs/store';
import { IFilters } from '@datx/jsonapi/interfaces/IFilters';
import { FreeFilterInputComponent } from '../../../utils/component/free-filter-input/free-filter-input.component';
import { PlayerMessageService } from '../../../core/services/player.service';
import { Approval } from '../../../core/types';
import { StateFilterCBComponent, getStatusColor, getStatusText } from '../../../utils/component/state-filter-combobox/state-cb.component';
import { ExerciseEntity } from '../../../lesson/exercise/exercise.entity';
import { MatDialog } from '@angular/material/dialog';
import { Observable, BehaviorSubject, combineLatest, from, map, switchMap, tap, firstValueFrom } from 'rxjs';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { ConsumerEntityService } from '../../../consumer/services/consumer.entity.service';
import { WidgetMessageService } from '../../../core/services/widget-message.service';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../../subject/subject.entity';
import { SequenceEntity } from '../../sequence.entity';
import { SequenceEntityService } from '../../services/sequence.entity.service';
import { EntityStateModel, InvalidateLoadedQueries } from '../../../core/state/entity.state';
import { makeQueryKey } from '../../../utils/entity-states/query-key';
import { ConfirmDialogComponent } from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { SimpleConfirmData } from '../../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { TranslocoModule } from '@jsverse/transloco';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TableLoadingSpinnerComponent } from '../../../utils/component/table-loading-spinner/table-loading-spinner';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { ExerciseSequenceEditComponent } from '../exercise-sequence-edit/exercise-sequence-edit.component';
import { HasApprovalState, StateBadgeComponent } from '../../../core/components/state-badge/state-badge.component';
import { BaseFilterComponent } from '../../../utils/component/base-filter-component/base-filter-component';

@Component({
  selector: 'pixl-exercise-sequence-main',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatTooltip,
    MatSelectModule,
    DatePipe,
    FreeFilterInputComponent,
    StateFilterCBComponent,
    AsyncPipe,
    MatPaginator,
    TableLoadingSpinnerComponent,
    TranslocoModule,
    StateBadgeComponent
  ],
  templateUrl: './exercise-sequence-main.component.html',
  styleUrl: './exercise-sequence-main.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExerciseSequenceMainComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  public sort = viewChild(MatSort);

  totalItems = 1000;

  private readonly store = inject(Store);
  public exerciseSequenceService = inject(SequenceEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);
  exerciseSequences$!: Observable<SequenceEntity[]>;
  private dialog = inject(MatDialog);
  deletedIds$ = new BehaviorSubject<string[]>([]);
  public refresh$ = new BehaviorSubject<void>(undefined);
  private readonly pageChange$ = toObservable(this.pageChange);
  private localStorageService = inject(LocalStorageService);

  exercises$!: Observable<ExerciseEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;

  public cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private playerService = inject(PlayerMessageService);

  public filters: IFilters = {};

  public displayedColumns: string[] = ['edit-actions', 'id', 'name', 'consumer', 'subject', 'tasks', 'state', 'kapiertImport', 'createdAt'];
  public dataSource = new MatTableDataSource<SequenceEntity>([] as SequenceEntity[]);
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;
  public readonly examSequenceStateClass = SequenceEntity as unknown as new () => HasApprovalState;

  ngOnInit() {
    this.loadRelationData();

    this.exerciseSequences$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumers,subjects,exercises',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'filter[kind]': 'exercise',
          'sort': 'name'
        };

        if (this.freeFilter?.trim()?.length >= this.freeFilterLen) {
          query['filter[idOrNameLike]'] = this.freeFilter.trim();
        }

        if (this.statusFilter?.length > 0) {
          query['filter[approval]'] = this.statusFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['filter[consumers][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['filter[subjectId]'] = this.subjectFilter.join(',');
        }

        return from(this.exerciseSequenceService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.exerciseSequenceService.allByQuery(query))),
          map((examSequences: SequenceEntity[]) => examSequences.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.exerciseSequenceService.stateClass) as EntityStateModel<SequenceEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state?.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator()?.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );
  }

  addExerciseSequence(): void {
    const dialogRef = this.dialog.open(ExerciseSequenceEditComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: {
        kind: 'exercise'
      }
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<SequenceEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new SequenceEntity(), result);
        await this.exerciseSequenceService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.widgetService.setMessage({
          message: 'Übungssequenz erfolgreich erstellt',
          messageTye: 'mtSuccess'
        });
      }
    });
  }

  public async copyExerciseSequence(examSequence: SequenceEntity) {
    const copyEntityInstance = Object.assign(new SequenceEntity(), examSequence);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.name = `${copyEntityInstance.name} (Kopie)`;
    copyEntityInstance.version = 1;
    await this.exerciseSequenceService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: 'Übungssequenz erfolgreich dupliziert.',
      messageTye: 'mtSuccess'
    });
  }

  public async deleteExerciseSequence(exerciseSequence: SequenceEntity): Promise<void> {
    if (!exerciseSequence?.id) {
      console.error('ID is undefined!', exerciseSequence);
      return;
    }

    this.currentSelectedRow.set(exerciseSequence.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogComponent, SimpleConfirmData, boolean>(ConfirmDialogComponent, {
          data: {
            name: String(exerciseSequence.name),
            headerText: 'Übungssequenz löschen'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.exerciseSequenceService.delete(exerciseSequence.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: 'Übungssequenz erfolgreich gelöscht.',
      messageTye: 'mtSuccess'
    });
  }

  editExerciseSequence(exerciseSequence: SequenceEntity): void {
    this.currentSelectedRow.set(exerciseSequence.id);
    const dialogRef = this.dialog.open(ExerciseSequenceEditComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: { ...exerciseSequence }
    });

    dialogRef.afterClosed().subscribe(async (updatedExamSequence: SequenceEntity | undefined) => {
      if (!updatedExamSequence) return;
      await firstValueFrom(this.exerciseSequenceService.update(updatedExamSequence));
      this.refresh$.next();

      this.widgetService.setMessage({
        message: 'Übungssequenz erfolgreich editiert',
        messageTye: 'mtSuccess'
      });
    });
  }

  getConsumerName(exerciseSequenceConsumerId: string) {
    if (!exerciseSequenceConsumerId) return '---';

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(exerciseSequenceConsumerId);

    return consumer?.name || exerciseSequenceConsumerId;
  }

  getSubjectName(exerciseSequenceSubjectId: string) {
    if (!exerciseSequenceSubjectId) return '---';

    const state = this.store.selectSnapshot(this.subjectService.stateClass);
    const subject = this.subjectService.stateClass.byId(state)(exerciseSequenceSubjectId);

    return subject?.name || exerciseSequenceSubjectId;
  }

  getAmountOfTasks(exerciseSequenceList: string[]): number {
    let result = 0;
    exerciseSequenceList.forEach(item => {
      result += item.length;
    });
    return result;
  }

  getKapiertImport(exerciseSequence: SequenceEntity): string {
    if (exerciseSequence?.consumerData?.kapiertImport) {
      return 'Ja';
    }
    return 'Nein';
  }

  getCreationDate(exerciseSequenceId: string): Date {
    return new Date(parseInt(exerciseSequenceId.substring(0, 8), 16) * 1000);
  }

  public getStatusColor(approval: Approval): string {
    return getStatusColor(approval);
  }

  public getStatusText(approval: Approval): string {
    return getStatusText(approval);
  }

  trackById(index: number, exerciseSequence: SequenceEntity): string {
    return exerciseSequence.id;
  }

  public previewPlayer2(sequence: SequenceEntity) {
    this.currentSelectedRow.set(sequence.id);
    this.playerService.openPlayerPreview(sequence.consumerId!, 'sequence', sequence.id);
  }
}
